import React from "react";
import { Preloader } from "react-materialize";
import $ from "jquery";
import { firebase } from "../../firebase/config";
import { sendBeginCheckout, tierValueAud } from "../../utils/analytics";

const stripeCreateCheckoutSessionUrl =
  "https://us-central1-bridgechampions.cloudfunctions.net/stripeCreateCheckoutSession";

class StripeCheckout extends React.Component {
  state = {
    loading: false,
    // Set once Stripe has given us a session URL. On iOS Safari the redirect
    // below is issued from an ajax callback, long after the tap that started
    // it, and it does not always take - the page just sits on "Loading..."
    // with a perfectly good session going unused. A member hit exactly that on
    // 31 Aug 2026, tried twice, and abandoned both times. So we keep the URL
    // and, if we are still here a moment later, offer it as a real link the
    // member can tap. A tap is a fresh user gesture and always navigates.
    checkoutUrl: null,
  };

  componentWillUnmount() {
    this.unmounted = true;
    if (this.redirectFallbackTimer) clearTimeout(this.redirectFallbackTimer);
  }

  normalizeCode = (code) => String(code || "").toLowerCase().replace(/\s+/g, "").trim();
  normalizeAmount = (amount) => {
    const n = Number(amount);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  capturePromoAuditOnMember = async ({
    enteredPromoCode,
    appliedPromoCode,
    stripeSessionId = "",
    stage = "checkout_session_created",
  }) => {
    if (!this.props.uid || !enteredPromoCode) return;
    try {
      await firebase
        .firestore()
        .collection("members")
        .doc(this.props.uid)
        .set(
          {
            lastPromoCodeEntered: enteredPromoCode,
            lastPromoCodeApplied: appliedPromoCode || enteredPromoCode,
            lastPromoCaptureStage: stage,
            lastPromoCaptureSessionId: stripeSessionId || "",
            lastPromoCaptureAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
    } catch (e) {
      console.error("Failed to capture promo audit on members doc", e);
    }
  };

  capturePromoUsage = async ({ response, postData, enteredPromoCode, appliedPromoCode }) => {
    if (!this.props.uid) return;
    if (!enteredPromoCode) return;
    try {
      const sessionId = response?.sessionId || "";
      const docId = sessionId
        ? `stripe_${sessionId}`
        : `stripe_${this.props.uid}_${Date.now()}`;

      await firebase
        .firestore()
        .collection("promoCodeUsage")
        .doc(docId)
        .set(
          {
            uid: this.props.uid,
            email: this.props.email || "",
            promoCodeEntered: enteredPromoCode,
            promoCodeApplied: appliedPromoCode || enteredPromoCode,
            tierName: postData?.tierName || "",
            monthlyAmountAud: this.normalizeAmount(postData?.monthlyAmountAud),
            priceId: postData?.priceId || "",
            provider: "stripe",
            status: "checkout_session_created",
            stripeSessionId: sessionId,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );

      await this.capturePromoAuditOnMember({
        enteredPromoCode,
        appliedPromoCode,
        stripeSessionId: sessionId,
        stage: "checkout_session_created",
      });
    } catch (e) {
      console.error("Failed to capture promo usage", e);
    }
  };

  handleCheckout = () => {
    this.setState({ loading: true });
    this.props.processing();

    const postData = {
      priceId: this.props.tierPriceId || "price_1SXVk6E9mroRD7lKIHxCKA7c", // Premium fallback
      email: this.props.email,
      uid: this.props.uid,
      tierName: this.props.tierName || "Premium",
      monthlyAmountAud: this.normalizeAmount(this.props.tierPrice),
    };

    const coupon = this.props.getToken();
    const enteredPromoCode = this.normalizeCode(
      typeof this.props.getEnteredPromoCode === "function" ? this.props.getEnteredPromoCode() : ""
    );
    const appliedPromoCode = this.normalizeCode(coupon);
    if (coupon && coupon !== "") {
      postData.coupon = coupon;
    }

    if (enteredPromoCode) {
      // Capture an immediate audit trail before network/redirect paths.
      this.capturePromoAuditOnMember({
        enteredPromoCode,
        appliedPromoCode,
        stage: "checkout_started",
      });
    }

    console.log("Creating checkout session with data:", postData);

    $.ajax({
      url: stripeCreateCheckoutSessionUrl,
      method: "POST",
      contentType: "application/json",
      dataType: "json",
      data: JSON.stringify(postData),
    })
    .done(async (response) => {
        console.log("Checkout session response:", response);
        if (response && response.url) {
          if (enteredPromoCode) {
            // Await write before redirect so entered code is reliably persisted.
            await this.capturePromoUsage({
              response,
              postData,
              enteredPromoCode,
              appliedPromoCode,
            });
          }
          // Persist the Checkout Session ID locally so the /success page can verify/activate
          // even if the session_id query param is lost/truncated.
          try {
            if (this.props.redirectAfterCheckout) {
              localStorage.setItem("postCheckoutRedirectTo", this.props.redirectAfterCheckout);
            }
            if (response.sessionId) {
              localStorage.setItem("postCheckoutSessionId", response.sessionId);
              localStorage.setItem("lastStripeCheckoutSessionId", response.sessionId);
              // Track which user initiated checkout so we don't try to activate it for a different login.
              if (this.props.uid) {
                localStorage.setItem("postCheckoutExpectedUid", this.props.uid);
              }
            }
          } catch (e) {
            // ignore
          }
          // GA4 funnel: last observable step on our own domain before Stripe.
          try {
            sendBeginCheckout({
              tier: postData.tier || this.props.tier || "premium",
              value: tierValueAud(postData.tier || this.props.tier),
              promo_code: appliedPromoCode || undefined,
            });
          } catch (e) {
            // never block checkout on analytics
          }
          // Redirect to Stripe's hosted checkout page. If that has not taken
          // effect shortly, surface the link so it can be tapped instead.
          this.setState({ checkoutUrl: response.url });
          this.redirectFallbackTimer = setTimeout(() => {
            if (!this.unmounted) this.setState({ loading: false });
          }, 2500);
          window.location.href = response.url;
        } else if (response && response.error) {
          // Server returned an error
          console.error("Checkout session error:", response);
          const errorMsg = response.error || 'Unknown error';
          const errorDetails = response.details ? ` (${response.details})` : '';
          alert(`Error: ${errorMsg}${errorDetails}\n\nPlease check:\n1. Price IDs exist in Stripe Dashboard\n2. Stripe API key is correct\n3. Contact support if issue persists.`);
          this.setState({ loading: false });
          if (this.props.clearProcessing) this.props.clearProcessing();
        } else {
          console.error("Invalid response format:", response);
          alert("Invalid response from server. Please try again.");
          this.setState({ loading: false });
          if (this.props.clearProcessing) this.props.clearProcessing();
        }
      })
    .fail((jqXHR, textStatus, errorThrown) => {
        this.setState({ loading: false });
        if (this.props.clearProcessing) this.props.clearProcessing();
        console.error("Error creating checkout session:", {
          status: jqXHR.status,
          statusText: jqXHR.statusText,
          responseText: jqXHR.responseText,
          textStatus,
          errorThrown
        });
        
        let errorMessage = "Unknown error";
        let errorDetails = "";
        let fullError = "";
        
        try {
          const errorResponse = JSON.parse(jqXHR.responseText);
          errorMessage = errorResponse.error || errorThrown || textStatus;
          errorDetails = errorResponse.details || errorResponse.code || "";
          fullError = JSON.stringify(errorResponse, null, 2);
        } catch (e) {
          errorMessage = jqXHR.responseText || errorThrown || textStatus;
          fullError = jqXHR.responseText || errorThrown;
        }
        
        // Show detailed error in console and alert
        console.error("Full error details:", fullError);
        
        if (jqXHR.status === 0) {
          alert("Unable to connect to payment server. Please check your internet connection and try again.");
        } else if (jqXHR.status === 500) {
          const alertMsg = `Server Error (500): ${errorMessage}${errorDetails ? '\nDetails: ' + errorDetails : ''}\n\nCheck browser console (F12) for full error details.\n\nCommon issues:\n- Price ID doesn't exist in Stripe\n- Stripe API key incorrect\n- Invalid session parameters`;
          alert(alertMsg);
        } else if (jqXHR.status === 400) {
          const detailMsg = errorDetails ? `\n\n${errorDetails}` : "\n\nPlease check that all required fields are provided.";
          alert(`Invalid Request: ${errorMessage}${detailMsg}`);
        } else {
          alert(`Payment Error (${jqXHR.status}): ${errorMessage}\n\nPlease try again or contact support.`);
        }
      });
  };

  render() {
    // The redirect did not take. Give them something to tap rather than a
    // spinner that never resolves.
    if (this.state.checkoutUrl && !this.state.loading) {
      return (
        <a
          className="PremiumMembership-stripe-button"
          href={this.state.checkoutUrl}
          style={{ display: "block", textAlign: "center", textDecoration: "none" }}
        >
          <span className="PremiumMembership-stripe-buttonTitle">
            Continue to secure checkout &rarr;
          </span>
          <span className="PremiumMembership-stripe-buttonSub">
            Tap to finish your payment with Stripe
          </span>
        </a>
      );
    }
    return (
      <button
        className="PremiumMembership-stripe-button"
        onClick={this.handleCheckout}
        disabled={this.state.loading}
      >
        {this.state.loading ? (
          <>
            <Preloader color="white" size="small" />
            <span style={{ marginLeft: '0.5rem' }}>Loading...</span>
          </>
        ) : (
          <>
            {/* Yearly has no trial — it is charged straight away, so the
                trial wording must never show on that tier's button. */}
            <span className="PremiumMembership-stripe-buttonTitle">
              {this.props.buttonTitle || (this.props.isYearly ? "Get Premium Yearly" : "Start 7-day free trial")}
            </span>
            <span className="PremiumMembership-stripe-buttonSub">Secure checkout with Stripe</span>
          </>
        )}
      </button>
    );
  }
}

export default StripeCheckout;
